import { Link } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { siteInfo } from '@/mocks/site';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ContactForm from '@/pages/contact/components/ContactForm';

const DOMAIN = 'https://97go.cn';

const channels = [
  {
    icon: 'ri-mail-send-line',
    title: '线索投稿邮箱',
    value: siteInfo.email,
    desc: '适合长文描述、附件较多的线索，发送后同样会在 1 个工作日内查看。',
  },
  {
    icon: 'ri-shield-user-line',
    title: '匿名爆料',
    value: '表单中不填称呼与邮箱即可',
    desc: '如果你不方便留联系方式，可以不填称呼和邮箱，我们依然会核实内容本身。',
  },
  {
    icon: 'ri-briefcase-4-line',
    title: '内容与商务合作',
    value: '合作请联系同一邮箱',
    desc: '包括内容授权、专题共建与频道合作，请在邮件标题注明合作方向。',
  },
];

export default function ContactPage() {
  useSEO({
    title: '联系我们 - 线索投稿与匿名爆料通道 | 爆料前线',
    description:
      '想给爆料前线投稿？这里提供线索投稿表单与联系邮箱，支持匿名爆料。请尽量提供时间、地点与可核实细节，编辑团队会优先处理信息完整的线索。',
    keywords: '联系爆料前线,线索投稿,匿名爆料,爆料平台,投稿通道',
    path: '/contact',
    ogType: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系我们',
        url: `${DOMAIN}/contact`,
        inLanguage: 'zh-CN',
        description: '爆料前线的线索投稿表单、匿名爆料通道与联系邮箱。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '爆料前线',
        url: `${DOMAIN}/`,
        email: siteInfo.email,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: '线索投稿',
          email: siteInfo.email,
          availableLanguage: 'zh-CN',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${DOMAIN}/` },
          { '@type': 'ListItem', position: 2, name: '联系我们', item: `${DOMAIN}/contact` },
        ],
      },
    ],
  });

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '联系我们' }]} />
          <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            手里有正在发生的一手信息，或者发现某条话题的细节有出入，都可以直接告诉我们。信息越具体，核实得越快。
          </p>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                除表单外，也可以通过邮箱联系编辑团队，我们会在工作时段内回复。
              </p>
              <ul className="mt-6 flex flex-col gap-4">
                {channels.map((item) => (
                  <li key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-heading text-sm font-bold text-foreground-950">{item.title}</h3>
                      <p className="mt-1 break-words text-sm text-foreground-800">{item.value}</p>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-6 rounded-lg border border-background-200 bg-secondary-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">投稿前的小建议</h2>
                <ol className="mt-3 flex flex-col gap-2.5 text-sm leading-relaxed text-foreground-700">
                  <li className="flex gap-2">
                    <span className="text-primary-600">01</span>
                    写清楚时间点，例如「9 月 10 日 21 点左右」。
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary-600">02</span>
                    注明地点或涉及范围，方便编辑判断影响面。
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary-600">03</span>
                    区分「亲眼看到」和「听说」，方便我们标注进展状态。
                  </li>
                </ol>
                <Link
                  to="/about"
                  className="mt-4 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-primary-700 transition-colors hover:text-primary-600"
                >
                  看看我们的内容原则
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-line text-base" />
                  </span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}